import * as React from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { X } from 'lucide-react';

import { cn } from '../../utils/cn';
import { DrawerPortal } from './drawer-portal';
import type { drawerOverlayVariants } from './drawer-overlay';
import { DrawerOverlay } from './drawer-overlay';
import { DrawerClose } from './drawer-close';
import { DrawerHandle } from './drawer-handle';
import { isDrawerOutsideInteractionTarget } from './drawer-outside-interaction';

const drawerVariants = cva(
    'rocco-drawer group/drawer-content fixed z-modal flex h-auto touch-none flex-col outline-hidden data-[vaul-drawer-direction=bottom]:inset-x-0 data-[vaul-drawer-direction=bottom]:bottom-0 data-[vaul-drawer-direction=bottom]:rounded-t-lg data-[vaul-drawer-direction=left]:inset-y-0 data-[vaul-drawer-direction=left]:left-0 data-[vaul-drawer-direction=left]:w-3/4 data-[vaul-drawer-direction=right]:inset-y-0 data-[vaul-drawer-direction=right]:right-0 data-[vaul-drawer-direction=right]:w-3/4 data-[vaul-drawer-direction=top]:inset-x-0 data-[vaul-drawer-direction=top]:top-0 data-[vaul-drawer-direction=top]:rounded-b-lg data-[vaul-drawer-direction=left]:sm:max-w-sm data-[vaul-drawer-direction=right]:sm:max-w-sm',
    {
        variants: {
            variant: {
                default: 'bg-background',
                glassy: 'bg-transparent pb-0 md:pb-6',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    },
);

const drawerContentVariants = cva('flex h-full flex-col', {
    variants: {
        variant: {
            default: 'w-full',
            glassy: 'mx-auto w-fit rounded-t-2xl bg-gray-100/85 shadow-lg backdrop-blur md:rounded-2xl',
        },
    },
    defaultVariants: {
        variant: 'default',
    },
});

interface DrawerContentProps
    extends
        React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content>,
        VariantProps<typeof drawerContentVariants>,
        VariantProps<typeof drawerOverlayVariants> {
    hideDefaultCloseButton?: boolean;
    noOverlay?: boolean;
    noHandle?: boolean;
    limitMaxHeight?: boolean;
    scrollable?: boolean;
    closeButtonClassName?: string;
    closeButtonIconClassName?: string;
    /** Renders above the scrollable drawer body, in the handle slot. */
    leadingContent?: React.ReactNode;
}

export const DrawerContent = React.forwardRef<
    React.ElementRef<typeof DrawerPrimitive.Content>,
    DrawerContentProps
>(
    (
        {
            className,
            children,
            variant,
            overlayVariant,
            hideDefaultCloseButton = false,
            noOverlay = false,
            noHandle = false,
            limitMaxHeight = false,
            scrollable = false,
            closeButtonClassName,
            closeButtonIconClassName,
            leadingContent,
            onPointerDownOutside,
            style,
            ...props
        },
        ref,
    ) => (
        <DrawerPortal data-slot="drawer-portal">
            {!noOverlay && <DrawerOverlay overlayVariant={overlayVariant} />}
            <DrawerPrimitive.Content
                ref={ref}
                data-slot="drawer-content"
                onPointerDownOutside={(event) => {
                    onPointerDownOutside?.(event);

                    const target =
                        (event.detail.originalEvent
                            .target as EventTarget | null) ?? null;
                    if (isDrawerOutsideInteractionTarget(target)) {
                        event.preventDefault();
                    }
                }}
                className={cn(
                    drawerVariants({ variant, className }),
                    // Keep max height in sync with mobile header height.
                    // packages/rocco/src/ui/website-header/components/MobileHeader.tsx
                    limitMaxHeight && 'max-h-[calc(100dvh-4.75rem)]',
                )}
                // vaul injects touch-action:none at runtime; set it here too so
                // real devices get drag gestures when that stylesheet is absent.
                style={{ touchAction: 'none', ...style }}
                {...props}
            >
                <div
                    className={cn(
                        drawerContentVariants({ variant }),
                        scrollable && 'flex min-h-0 flex-col',
                    )}
                >
                    {!leadingContent && !noHandle && <DrawerHandle />}
                    {leadingContent}
                    <div
                        className={cn(
                            // Non-scrollable drawers must keep children as direct flex
                            // items of the h-full column above, or flex-1/min-h-0 height
                            // chains inside them collapse (e.g. HDE registration footer
                            // pushed off-screen on mobile).
                            scrollable
                                ? 'min-h-0 flex-1 overflow-y-auto'
                                : 'contents',
                        )}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {children}
                        {!hideDefaultCloseButton && (
                            <DrawerClose
                                className={cn(
                                    'absolute top-6 right-5 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none data-[state=open]:bg-secondary md:top-7 md:right-6',
                                    closeButtonClassName,
                                )}
                            >
                                <X
                                    className={cn(
                                        'h-6 w-6',
                                        closeButtonIconClassName,
                                    )}
                                />
                                <span className="sr-only">Close</span>
                            </DrawerClose>
                        )}
                    </div>
                </div>
            </DrawerPrimitive.Content>
        </DrawerPortal>
    ),
);

DrawerContent.displayName = 'DrawerContent';
